import { Container } from 'pixi.js';
import {
  StudioAnimation,
  StudioImageShapeDescriptor,
  StudioScene,
} from '../../../../store/studio/studio.types';
import {
  _screenX,
  _screenY,
} from '../../../../../services/canvas/canvas.service';
import { StudioSceneShapeCell } from './studio-scene-shape-cell';
import {
  deleteVideoInstance,
  saveScene,
  StudioService,
  StudioVideoShapeDescriptor,
  setCurrentStudioSceneId,
  addLastStudioScene,
  StudioSceneBackgroundDefinition,
} from '../../../../store/studio';
import { cloneDeep, isEqual, omit } from 'lodash';
import gsap from 'gsap';
import {
  StudioTextShape,
  StudioTextShapeDescriptor,
} from '../../../../store/studio/text/studio-text-shape';
import { VideoShape } from '../../base/video-shape';
import { StudioImageShape } from '../../../../store/studio/shapes/image/studio-image.shape';
import { RectangleElement } from '../../primitive/rectangle-element';
import { SceneTransition } from '../../../../animation/text-animation.service';
import { TextElement } from '../../primitive/text-element';
import { PathElement } from '../../primitive/path-element';
import {
  StudioElementShape,
  StudioElementShapeDescriptor,
} from '../../../../store/studio/element/studio-element-shape';
import { LayoutCellDefinition, RootLayoutCell } from '../../../../store/studio/layout/layout-cell';
import { AnimationContext } from '../../../../store/studio/animation/studio-animation.service';
import { CanvasBackground } from '../../../canvas-background';
export interface StudioSceneShapeConfig {
  margin?: number;
  preview?: boolean;
}
//
export class StudioSceneShape {
  container: Container;
  shapeCells: Record<string, StudioSceneShapeCell> = {};
  maskRectangle: RectangleElement;
  background: CanvasBackground | null = null;
  shapes: Record<
    string,
    StudioTextShape | VideoShape | StudioImageShape | StudioElementShape
  > = {};
  private _shapes: any[] = [];
  get cs() {
    return this.shapeService.cs;
  }

  get store() {
    return this.shapeService.store;
  }

  get animationService() {
    return this.shapeService.animationService;
  }

  get newCellAlignment() {
    // const margin = this.config?.margin ?? 0.1;
    const margin = this.config?.margin ?? 0;
    const mx = _screenX * margin;
    const my = _screenY * margin;

    if (this.cs.isPortrait) {
      return {
        x: mx,
        y: my,
        w: this.cs.portraitScreenX - 2 * mx,
        h: this.cs.portraitScreenY - 2 * mx,
      };
    }

    return {
      x: mx,
      y: my,
      w: this.cs._screenX - 2 * mx,
      h: this.cs._screenY - 2 * my,
    };
  }
  get index() {
    return this.scene.index;
  }

  get shapeService() {
    return this.studioService.shapeService;
  }

  get positionIndex() {
    return this.scene.positionIndex;
  }
  defaultPosition = 0;

  get orientationService() {
    return {} as any;
  }
  // general-shape methods
  getType() {
    return 'studio-scene-shape';
  }

  get groupShapeParent() {
    return null;
  }

  localStartDrag() {
    this.shapeService.startDrag(this);
  }

  circleContainer: Container;
  auxCircleContainer: Container;

  get x() {
    return this.container.position.x;
  }
  get y() {
    return this.container.position.y;
  }

  get padding() {
    return this.scene.padding ?? 0.05;
  }

  get xOffset() {
    return this.padding * this.cs.currentScreenX;
  }
  get yOffset() {
    return this.padding * this.cs.currentScreenX;
  }

  get screenWidth() {
    const full = this.cs.currentScreenX;
    return (1 - this.padding * 2) * full;
  }

  get screenHeight() {
    const full = this.cs.currentScreenY;
    const padding = this.padding * this.cs.currentScreenX;
    return full - 2 * padding;
  }

  hideOrientationLines() {
    // Object.values(this.shapeCells).forEach(cell => cell.hideOrientationLines()); //
  }
  get prev() {
    return this.studioService.scenesByPositionIndex[
      this.scene.positionIndex - 1
    ] as StudioSceneShape;
  }
  get next() {
    return this.studioService.scenesByPositionIndex[
      this.scene.positionIndex + 1
    ] as StudioSceneShape;
  }
  get loadPromise() {
    // console.log('shapeCells', this.shapeCells);
    return Promise.all(
      Object.values(this.shapes).map(cell => cell.loadPromise),
    );
  }
  get service() {
    return this.studioService.shapeService;
  }

  get isAddAfterLast() {
    return this.scene.id === 'add-after-last';
  }

  layout: RootLayoutCell;
  animationByLayout: StudioAnimation;
  constructor(
    public readonly studioService: StudioService,
    public scene: StudioScene,
    private config?: StudioSceneShapeConfig,
  ) {
    try {
      if (this.scene.layoutDefinition) {
        this.layout = new RootLayoutCell(
          this.scene,
          this.scene.layoutDefinition,
          { W: this.screenWidth, H: this.screenHeight },
        );
        // this.animationByLayout = this.layout.getAnimation();
      }
    } catch (error) {
      console.log('error', error);
    }

    this.initContainers();
    this.initShapes();
    this.calcAnimationTime();

    if (this.isAddAfterLast) {
      this.jnitAddNewScene();
    }
  }

  jnitAddNewScene() {
    this.hide();
    // -- //

    let x: number, y: number, w: number, h: number;

    if (this.cs.isPortrait) {
      w = this.cs.currentScreenX * 0.6;
      h = this.cs.currentScreenY * 0.2;
      x = this.cs.currentScreenX * 0.2;
      y = this.cs.currentScreenY * 0.4;
    } else {
      w = this.cs.currentScreenX * 0.6;
      h = this.cs.currentScreenY * 0.2;
      x = this.cs.currentScreenX * 0.2;
      y = this.cs.currentScreenY * 0.4;
    }

    // Plus icon dimensions
    const iconSize = 32;
    const textX = x + w / 2 - 100;
    const textY = y + h / 2 - 21;
    const iconX = textX + 180;
    const iconY = y + h / 2 - iconSize / 2;

    const rectangle = new RectangleElement(this, this.container, {
      position: {
        x,
        y,
      },
      width: w,
      height: h,
      fill: '#ffffff',
      'fill-opacity': 0.001,
      r: 24,
    });

    rectangle.element.interactive = true;
    rectangle.element.cursor = 'pointer';

    this.container.alpha = 0.5;
    this.bgContainer.alpha = 0.3;
    rectangle.element.addEventListener('mouseenter', () => {
      this.container.alpha = 0.8;
    });
    rectangle.element.addEventListener('mouseleave', () => {
      this.container.alpha = 0.5;
    });

    rectangle.element.onclick = () => {
      this.store.dispatch(addLastStudioScene());
      if (this.cs.isPortrait) {
        this.container.position.x -= this.cs.sceneWithGap;
        this.bgContainer.position.x -= this.cs.sceneWithGap;
      } else {
        this.container.position.y -= this.cs.sceneWithGap;
        this.bgContainer.position.y -= this.cs.sceneWithGap;
      }
      //this.jnitAddNewScene();
    };

    this.service.fontService._loadFont('Goldman').then(() => {
      new TextElement(this, this.container, {
        x: 420,
        y: textY,
        fontFamily: 'Goldman',
        text: 'New scene',
        fontStyle: 'normal',
        fill: '#000000',
        fontSize: 42,
        fontWeight: '400',
      });
    });

    // Draw plus icon - horizontal line
    new PathElement(this, this.container, {
      position: { x: iconX, y: iconY + iconSize / 2 },
      stroke: '#000000',
      'stroke-width': 3,
      elements: [{ type: 'line', x: iconSize, y: 0 }],
    });

    // Draw plus icon - vertical line
    new PathElement(this, this.container, {
      position: { x: iconX + iconSize / 2, y: iconY },
      stroke: '#000000',
      'stroke-width': 3,
      elements: [{ type: 'line', x: 0, y: iconSize }],
    });
  }

  async resetAnimation() {
    this.container.alpha = 1;
    // this.initShapes();

    await Promise.all(
      Object.values(this.shapes).map(shape => {
        if (shape.getType() == 'video-shape') {
          (shape as VideoShape).resetAnimation();
        }
        return shape.reset();
      }),
    );
  }

  reset() {
    Object.values(this.shapes).forEach(shape => {
      shape.reset();
    });
  }

  animationDuration: number;
  calcAnimationTime() {
    const timeline = this.pseudoAnimation;
    this.animationDuration = timeline.duration();
    return this.animationDuration;
  }

  getAbsoluteStartTime() {
    let time = 0;
    return 0;
    let prev = this.prev;
    while (prev) {
      time += prev.animationDuration || 0;
      prev = prev.prev;
    }
    return time;
  }
  disablePositionUpdate = false;
  disablePositionUpdateForPrevs() {
    this.disablePositionUpdate = true;
    this.prev?.disablePositionUpdateForPrevs();
  }

  enablePositionUpdateForPrevs() {
    this.disablePositionUpdate = false;
    this.prev?.enablePositionUpdateForPrevs();
  }

  disablePositionUpdateForNexts() {
    this.disablePositionUpdate = true;
    this.next?.disablePositionUpdateForNexts();
  }

  enablePositionUpdateForNexts() {
    this.disablePositionUpdate = false;
    this.next?.enablePositionUpdateForNexts();
  }

  updateDefaultPosition() {
    this.defaultPosition = this.scene.positionIndex * this.cs.sceneWithGap;
  }

  /**
   * Start the local timer that counts from 0 to 1
   */

  /**
   * Reset the local timer to 0
   */
  get pseudoAnimation(): gsap.core.Timeline {
    return this.getPseudoAnimation(this.scene.animation);
  }

  getPseudoAnimation(animation: StudioAnimation) {
    if (!animation) {
      const timeline = gsap.timeline();
      timeline.add(gsap.to({}, { duration: 1 }));
      return timeline;
    }

    // -- // -- //
    const { target, next, paralell } = animation;

    const timeline =
      this.shapes[target]?.pseudoAnimation(animation) || gsap.timeline();

    if (next) {
      const nextTimeline = this.getPseudoAnimation(next);
      if (next.delay) {
        timeline.add(nextTimeline, `+=${next.delay}`);
      } else {
        timeline.add(nextTimeline, 0);
      }
    }
    if (paralell) {
      // const paralellTimeline = gsap.timeline();
      // addToTimeLine(paralellTimeline, paralell);
      // timeline.add(paralellTimeline, 0);
    }
    return timeline;
  }

  set localTime(value: number) {
    this.studioService.sceneLocalTime = value;
  }

  getTargetIRI(target: string) {
    if (target?.includes(':')) {
      const [type, index] = target.split(':');
      switch (type) {
        case 'text':
          return this.scene.texts?.[+index];
        case 'image':
          return this.scene.images?.[+index];
        case 'video':
          return this.scene.videoInstances?.[+index];
      }
    }
    return target;
  }

  gsapFunctionsAnimation({
    animation,
    functions,
  }: {
    animation: StudioAnimation;
    functions: Record<string, StudioAnimation>;
  }) {
    const { function: functionTarget } = animation;
    const fcnAnimation = functions[functionTarget];
    if (!fcnAnimation) {
      console.warn('Function not found', functionTarget);
      return gsap.timeline();
    }

    const fcnTimeline = this.gsapAnimate({
      animation: fcnAnimation,
      functions,
    });

    const { next, paralell } = animation;
    if (next) {
      const { delay, overlap } = next;
      const offset = overlap ? `-=${overlap}` : `+=${delay || 0}`;
      const nextTimeline = this.gsapAnimate({
        animation: next,
        functions,
      });
      fcnTimeline.add(nextTimeline, offset);
    }

    if (paralell) {
      const paralellTimeline = this.gsapAnimate({
        animation: paralell,
        functions,
      });
      const offset = paralell.delay || 0;
      fcnTimeline.add(paralellTimeline, paralell.delay || 0);
    }

    return fcnTimeline;
  }

  timeline: gsap.core.Timeline;
  gsapAnimate(params?: {
    animation?: StudioAnimation;
    complete?: boolean;
    functions?: Record<string, StudioAnimation>;
  }) {
    // console.log('gsapAnimate', params?.animation);
    const timeline = gsap.timeline();
    const { texts, images, videoInstances, elements } = this.scene;
    const animation =
      params?.animation ||
      this.animationByLayout ||
      this.scene.animation ||
      ({
        target: texts?.[0] || images?.[0] || videoInstances?.[0],
        duration: 1,
        type: 'appear',
        sceneTransition: {
          duration: 1,
        },
      } as StudioAnimation);

    if (videoInstances?.length) {
      // -- // -- //
      const [videoId] = videoInstances;
      const videoShape = this.shapes[videoId] as VideoShape;
      if (videoShape) {
        const videoTimeline = videoShape.gsapAnimateStep(animation);
        timeline.add(videoTimeline, 0);
      }
    }

    // console.log('gsapAnimate', animation); //
    const { complete } = params || {};

    let { functions } = params || {};
    if (!animation) {
      console.warn('Animation is not defined');
      return gsap.timeline();
    }
    const {
      target,
      sceneTransition,
      function: functionTarget,
      functions: baseFunctions,
    } = animation;

    functions ||= baseFunctions;

    if (functionTarget) {
      const fcnAnimation = functions[functionTarget];
      const fcnTimeline = this.gsapFunctionsAnimation({
        animation,
        functions,
      });
      timeline.add(fcnTimeline, 0);
    }

    if (target === '__background__' && this.background) {
      const bgTimeline = this.animateBackground();
      if (bgTimeline) {
        timeline.add(bgTimeline, 0);
      }
    } else if (target) {
      let targetId: string;
      if (target.includes(':')) {
        const [type, index] = target.split(':');
        switch (type) {
          case 'text':
            targetId = texts?.[+index];
            break;
          case 'image':
            targetId = images?.[+index];
            break;
          case 'video':
            targetId = videoInstances?.[+index];
            break;
          case 'element':
            targetId = elements?.[+index];
            break;
        }
      } else {
        targetId = target;
      }

      const shape = this.shapes[targetId];
      if (!shape) {
        console.warn('Shape not found', target, 'targetId', targetId);
        return timeline;
      }

      if (animation.startPosition?.cell && 'alignInCell' in shape) {
        (shape as StudioTextShape).alignInCell(
          animation.startPosition.cell,
          true,
        );
      }
      // if (animation.startPosition?.opacity !== undefined) {
      //   (shape as any).container.alpha = animation.startPosition.opacity;
      // }
      // if (animation.startPosition?.zoom !== undefined) {
      //   (shape as any).container.scale.set(animation.startPosition.zoom);
      // }

      const shapeTimeline = shape.gsapAnimateStep(animation);
      timeline.add(shapeTimeline, 0);
    }

    if (complete) {
      this.animateSceneTransition(sceneTransition, timeline);
    }

    if (animation.next) {
      const { delay, overlap } = animation.next;
      const offset = overlap ? `-=${overlap}` : `+=${delay || 0}`;
      timeline.add(
        this.gsapAnimate({ complete, animation: animation.next }),
        offset,
      );
    }

    if (animation.paralell) {
      const { delay } = animation.paralell;
      timeline.add(
        this.gsapAnimate({ complete, animation: animation.paralell }),
        delay || 0,
      );
    }

    return timeline;
  }

  private animateBackground(): gsap.core.Timeline | null {
    const bg = this.background;
    const anim = this.scene.background?.animation;
    if (!bg || !anim) return null;

    const tl = gsap.timeline();
    const sprite = bg.bg;
    if (!sprite) return null;

    if (anim.crop) {
      const { x = 0, y = 0, scale = 1 } = anim.crop;
      tl.to(sprite, {
        x: sprite.x + x * _screenX,
        y: sprite.y + y * _screenY,
        duration: 2,
        ease: 'power2.inOut',
      }, 0);
      if (scale !== 1) {
        tl.to(sprite.scale, {
          x: sprite.scale.x * scale,
          y: sprite.scale.y * scale,
          duration: 2,
          ease: 'power2.inOut',
        }, 0);
      }
    }

    return tl;
  }

  animateSceneTransition(
    sceneTransition: SceneTransition,
    timeline: gsap.core.Timeline,
  ) {
    if (!this.next) {
      return;
    }

    console.log('animateSceneTransition', sceneTransition);
    if (!sceneTransition) {
      // -- //
      const startTimeline = gsap.to({}, { duration: 0.0001 });
      const sceneTransitionTimeline = gsap.to({}, { duration: 0 });
      //
      startTimeline.eventCallback('onStart', () => {
        console.log('sceneTransitionTimeline > on-start');

        this.store.dispatch(
          setCurrentStudioSceneId({ value: this.next.scene.id }),
        );
        this.moveNextScenes();
      });

      sceneTransitionTimeline.eventCallback('onComplete', () => {
        console.log('sceneTransitionTimeline > on-complete');
        this.movePrevScenes();
      });

      timeline.add(startTimeline);
      timeline.add(sceneTransitionTimeline);

      const next = this.next;
      if (next) {
        timeline.add(next.gsapAnimate({ complete: true }));
      }
    }
  }

  movePrevScenes() {
    let prev = this.prev;
    this.moveMe();
    while (prev) {
      if (this.cs.isPortrait) {
        prev.container.position.x -= this.cs.sceneWithGap;
        prev.bgContainer.position.x -= this.cs.sceneWithGap;
      } else {
        prev.container.position.y -= this.cs.sceneWithGap;
        prev.bgContainer.position.y -= this.cs.sceneWithGap;
      }
      prev = prev.prev;
    }
  }

  get offsetXY() {
    const offset = this.cs.currentScreenX * 0.05;
    return [offset, offset];
  }

  // get screenWidth() {
  //   const [xOffset] = this.offsetXY;
  //   return this.cs.currentScreenX - 2 * xOffset;
  // }

  // get screenHeight() {
  //   const [_, yOffset] = this.offsetXY;
  //   return this.cs.currentScreenY - 2 * yOffset;
  // }

  moveMe() {
    if (!this.next) {
      return;
    }
    if (this.cs.isPortrait) {
      this.cs.currentX -= this.cs.sceneWithGap;
      this.studioService.currentX -= this.cs.sceneWithGap;
      this.container.position.x -= this.cs.sceneWithGap;
      this.bgContainer.position.x -= this.cs.sceneWithGap;
      this.circleContainer.position.x -= this.cs.sceneWithGap;
    } else {
      this.cs.currentY -= this.cs.sceneWithGap;
      this.studioService.currentY -= this.cs.sceneWithGap;
      this.container.position.y -= this.cs.sceneWithGap;
      this.bgContainer.position.y -= this.cs.sceneWithGap;
      this.circleContainer.position.y -= this.cs.sceneWithGap;
    }
  }

  moveNextScenes() {
    let next = this.next;
    while (next) {
      if (this.cs.isPortrait) {
        next.container.position.x -= this.cs.sceneWithGap;
        next.bgContainer.position.x -= this.cs.sceneWithGap;
        next.circleContainer.position.x -= this.cs.sceneWithGap;
      } else {
        next.container.position.y -= this.cs.sceneWithGap;
        next.bgContainer.position.y -= this.cs.sceneWithGap;
        next.circleContainer.position.y -= this.cs.sceneWithGap;
      }
      next = next.next;
    }
  }

  bgContainer: Container;
  childrenContainer: Container;

  private initContainers() {
    // -- // -- // -- // -- // -- // -- //
    this.defaultPosition = this.scene.positionIndex * this.cs.sceneWithGap;

    this.container = new Container();
    this.bgContainer = new Container();
    this.circleContainer = new Container();

    this.childrenContainer = new Container();
    this.childrenContainer.position.x = this.xOffset;
    this.childrenContainer.position.y = this.yOffset;
    //if (this.scene.id !== 'add-after-last') {
    this.initBackground(this.scene.background);

    if (this.cs.isPortrait) {
      this.container.x =
        this.xOffset +
        this.defaultPosition +
        this.studioService.currentX / (this.shapeService.cs.fitScale || 1);
      this.container.y = 0;
    } else {
      this.container.x = 0;
      this.container.y =
        this.defaultPosition +
        this.studioService.currentY / (this.shapeService.cs.fitScale || 1);
    }

    this.bgContainer.position.x = this.container.position.x;
    this.bgContainer.position.y = this.container.position.y;

    this.circleContainer.position.x = this.container.position.x;
    this.circleContainer.position.y = this.container.position.y;

    if (!this.config?.preview) {
      this.cs.canvasContainer.addChild(this.container);
      this.cs.canvasBackgroundContainer.addChild(this.bgContainer);
      this.cs.canvasCircleContainer.addChild(this.circleContainer);
    }
  }

  mountPreview(stage: Container, scale: number) {
    this.container.position.set(0, 0);
    this.bgContainer.position.set(0, 0);
    this.container.scale.set(scale);
    this.bgContainer.scale.set(scale);
    stage.addChild(this.bgContainer);
    stage.addChild(this.container);
  }

  initBackground(def: StudioSceneBackgroundDefinition) {
    if (!def) {
      const fallbackContainer =
        this.service.backgroundService.initRandomColors(this.cs.isPortrait);
      this.bgContainer.addChild(fallbackContainer);
      this.background = null;
      return;
    }
    this.background = this.service.backgroundService._getInstance(
      def,
      { isPortrait: this.cs.isPortrait },
    );
    this.bgContainer.addChild(this.background.container);
  }

  patch(descriptor: StudioScene) {
    this.scene = descriptor;
    this.initShapes();
  }

  setPositionByCurrentY(currentY: number) {
    if (this.disablePositionUpdate) {
      return;
    }
    this.container.position.y = this.defaultPosition + currentY;
    this.bgContainer.position.y = this.defaultPosition + currentY;
    this.circleContainer.position.y = this.defaultPosition + currentY;

    const treshold = this.cs.sceneWithGap + this.cs.sceneGap;
    if (
      this.container.position.y < -treshold ||
      treshold < this.container.position.y
    ) {
      this.disableContainer(this.container);
      this.disableContainer(this.bgContainer);
      this.disableContainer(this.circleContainer);
    } else {
      this.enableContainer(this.container);
      this.enableContainer(this.bgContainer);
      this.enableContainer(this.circleContainer);
    }
  }

  disableContainer(container: Container) {
    container.visible = false;
    container.renderable = false;
    container.eventMode = 'none';
  }

  enableContainer(container: Container) {
    container.visible = true;
    container.renderable = true;
    container.eventMode = 'auto';
  }

  setPositionByCurrentX(currentX: number) {
    // console.log('setPositionByCurrentX', currentX);
    if (this.disablePositionUpdate) {
      return;
    }
    // console.log('setPositionByCurrentX > int', currentX);
    this.container.position.x = this.defaultPosition + currentX;
    this.bgContainer.position.x = this.defaultPosition + currentX;
    this.circleContainer.position.x = this.defaultPosition + currentX;
  }

  sortFcn(a, b) {
    const aIsText = a[0].includes('text');
    const bIsText = b[0].includes('text');

    // push text keys to the end
    if (aIsText && !bIsText) return 1;
    if (!aIsText && bIsText) return -1;

    const { w: aw, h: ah } = a[1].cell;
    const { w: bw, h: bh } = b[1].cell;
    // descending order
    return bw * bh - aw * ah;
  }

  initShapes() {
    const alreadyInitialized = new Set(Object.keys(this.shapes));

    this._shapes = [];
    const deleted: string[] = [];
    Object.entries(this.shapes || {}).forEach(([id, shape]) => {
      if (!this.scene.shapes[id]) {
        shape.remove();
        deleted.push(id);
        return;
      }
    });

    deleted.forEach(id => {
      delete this.shapes[id];
    });

    // if (this.layout) {
    //   try {
    //     const layoutCells = this.layout.shapeCells;
    //     Object.entries(this.scene.shapes).map(([id]) => {
    //       if (!layoutCells[id]) {
    //         return;
    //       }
    //       if (this.scene.shapes[id].cell?.custom) {
    //         return;
    //       }
    //       this.scene.shapes[id].cell = layoutCells[id];
    //     });
    //   } catch (error) {
    //     console.log('error > updating cells', error);
    //   }
    // }

    // Uniform font size: when multiple non-custom texts are in a layout,
    // compute each text's optimal font size and use the smallest so all
    // texts look consistent.
    // const layoutTexts = (this.scene.texts || []).filter(
    //   id => !this.scene.shapes[id]?.cell?.custom,
    // );
    // if (this.layout && layoutTexts.length > 1) {
    //   const sizes = layoutTexts.map(id => {
    //     const d = this.scene.shapes[id] as StudioTextShapeDescriptor;
    //     return StudioTextShape.getFontSizeByCell(
    //       this.cs,
    //       d.cell,
    //       d.text || '',
    //       d.fontFamily || 'Arial',
    //     );
    //   });
    //   const uniformSize = Math.min(...sizes);
    //   layoutTexts.forEach(id => {
    //     this.scene.shapes[id].cell.fontSize = uniformSize;
    //   });
    // }

    const { texts, images, videoInstances, elements } = this.scene;

    (videoInstances || []).forEach(id => {
      if (this.shapes[id]) {
        (this.shapes[id] as VideoShape).update(
          this.scene.shapes[id] as StudioVideoShapeDescriptor,
        );
        return;
      }
      this.shapes[id] = new VideoShape(
        this,
        id,
        cloneDeep(this.scene.shapes[id] as StudioVideoShapeDescriptor),
        undefined,
        { preview: this.config?.preview },
      );
    });

    (elements || []).forEach(id => {
      if (this.shapes[id]) {
        (this.shapes[id] as StudioElementShape).update(
          this.scene.shapes[id] as StudioElementShapeDescriptor,
        );
        return;
      }
      this.shapes[id] = new StudioElementShape(
        this,
        id,
        cloneDeep(this.scene.shapes[id] as StudioElementShapeDescriptor),
      );
    });

    (images || []).forEach(id => {
      if (this.shapes[id]) {
        (this.shapes[id] as StudioImageShape).update(
          this.scene.shapes[id] as StudioImageShapeDescriptor,
        );
        return;
      }
      this.shapes[id] = new StudioImageShape(
        this,
        id,
        cloneDeep(this.scene.shapes[id] as StudioImageShapeDescriptor),
        undefined,
        { preview: this.config?.preview },
      );
    });

    (texts || []).forEach(id => {
      if (this.shapes[id]) {
        (this.shapes[id] as StudioTextShape).update(
          this.scene.shapes[id] as StudioTextShapeDescriptor,
        );
        return;
      }
      this.shapes[id] = new StudioTextShape(
        this,
        id,
        cloneDeep(this.scene.shapes[id] as StudioTextShapeDescriptor),
        { preview: this.config?.preview },
      );
    });

    this.maskRectangle?.remove();

    const [width, height] = [this.cs.currentScreenX, this.cs.currentScreenY];
    // this.maskRectangle = new RectangleElement(this, this.container, {
    //   width,
    //   height,
    //   fill: '#000000',
    //   opacity: 0.001,
    //   r: 24,
    // });

    // Object.values(this.shapes).forEach(shape => {
    //   shape.container.mask = this.maskRectangle.element;
    // });

    const loadPromise = Promise.all(
      Object.values(this.shapes).map(shape => shape.loadPromise),
    );

    loadPromise.then(() => {
      // console.log('loadPromise > resolved');
      // Object.entries(this.scene.shapes)
      //   .sort(this.sortFcn)
      //   .forEach(([id]) => {
      //     this.shapes[id].initRC();
      //   });
    });

    // this.container.mask = this.maskRectangle.element;
  }
  // -- // -- // -- // -- //
  save() {
    this.store.dispatch(
      saveScene({
        sceneId: this.sceneId,
        value: cloneDeep(this.scene),
      }),
    );
  }

  updateScene(scene: StudioScene) {
    const { positionIndex } = this.scene;
    const oldBackgroundDefinitionId = this.scene.backgroundDefinitionId;

    if (this.scene.background !== scene.background) {
      // -- //
      this.initBackground(scene.background);
    }

    if (positionIndex != scene.positionIndex) {
      this.defaultPosition = scene.positionIndex * this.cs.sceneWithGap;
      // console.log('to', this.defaultPosition + this.studioService.currentY); // -- //
      gsap.to(this.container.position, {
        y:
          this.defaultPosition +
          this.studioService.currentY / this.shapeService.cs.fitScale,
        duration: 0.5,
        ease: 'power1.inOut',
      });
      if (this.bgContainer) {
        gsap.to(this.bgContainer.position, {
          y:
            this.defaultPosition +
            this.studioService.currentY / this.shapeService.cs.fitScale,
          duration: 0.5,
          ease: 'power1.inOut',
        });
      }
    }
    this.scene.positionIndex = scene.positionIndex;

    const newScene = omit(scene, 'positionIndex');
    const oldScene = omit(this.scene, 'positionIndex');
    if (isEqual(oldScene, newScene)) {
      return;
    }

    // console.log('compareShapes', isEqual(oldScene.shapes, newScene.shapes));

    // Check if background definition changed
    const backgroundChanged =
      oldBackgroundDefinitionId !== scene.backgroundDefinitionId;

    // If background changed, recreate the background container
    if (backgroundChanged && this.bgContainer) {
      this.cs.canvasContainer.removeChild(this.bgContainer);
      this.bgContainer.destroy({ children: true });
      this.bgContainer = null;

      // Create new background container
      this.bgContainer = this.cs.backgroundService.initById(
        scene.backgroundDefinitionId,
      );
      if (this.bgContainer) {
        this.cs.canvasContainer.addChild(this.bgContainer);
        this.bgContainer.position.x = 0;
        this.bgContainer.position.y =
          this.defaultPosition +
          this.studioService.currentY / (this.shapeService.cs.fitScale || 1);
      }
    }
    // const layoutChanged = !isEqual(
    //   oldScene.layoutDefinition,
    //   newScene.layoutDefinition,
    // );
    // if (layoutChanged) {
    //   this.scene = cloneDeep(scene);
    //   if (this.scene.layoutDefinition) {
    //     this.layout = new RootLayoutCell(
    //       this.scene,
    //       this.scene.layoutDefinition,
    //     );
    //   }
    //   Object.entries(this.shapes).forEach(([id, shape]) => {
    //     const cell = this.layout.shapeCells[id];
    //     shape.update({ ...this.scene.shapes[id], cell } as any);
    //   });
    //   return;
    // }

    if (isEqual(oldScene.shapes, newScene.shapes)) {
      this.scene = cloneDeep(scene);
      return;
    }

    for (const [id, shape] of Object.entries(this.shapes)) {
      if (!newScene.shapes[id]) {
        shape.remove();
        delete this.shapes[id];
      }

      if (isEqual(this.scene.shapes[id], newScene.shapes[id])) {
        continue;
      }

      if (!isEqual(this.scene.shapes[id].cell, newScene.shapes[id].cell)) {
        continue;
      }

      this.scene.shapes[id] = newScene.shapes[id];
      shape.update(newScene.shapes[id] as any);
    }

    // -- // -- // --
    this.scene = cloneDeep(scene);
    this.initShapes();
  }

  addVideo(id: string, descriptor: StudioVideoShapeDescriptor) {
    this.shapes[id] = new VideoShape(this, id, descriptor);
    this.scene.shapes[id] = descriptor;
    this.save();
  }
  removeVideo(index: number) {
    const videoId = `video:${index}`;

    if (!this.scene.shapes[videoId]) {
      console.warn('No video to remove in the scene descriptor');
      return;
    }

    const { videoInstanceId } = this.scene.shapes[
      videoId
    ] as StudioVideoShapeDescriptor;

    const instance = this.studioService.videoInstances[videoInstanceId];
    // -- //
    this.store.dispatch(
      deleteVideoInstance({
        videoInstanceId,
        videoId: instance?.mediaId,
      }),
    );
    this.shapes[videoId]?.remove();
    delete this.scene.shapes[videoId];

    this.scene.videoInstances = this.scene.videoInstances.filter(
      instanceId => instanceId !== videoInstanceId,
    );
    this.save();
  }

  addImage(id: string, descriptor: StudioImageShapeDescriptor) {
    this.shapes[id] = new StudioImageShape(this, id, descriptor);
    this.scene.shapes[id] = descriptor;
    this.save();
  }

  removeImage(index: number) {
    const imageId = `image:${index}`;

    if (!this.scene.shapes[imageId]) {
      console.warn('No image to remove in the scene descriptor');
      return;
    }

    const descriptor = this.scene.shapes[imageId] as StudioImageShapeDescriptor;

    this.shapes[imageId]?.remove();
    delete this.scene.shapes[imageId];

    this.scene.images = (this.scene.images || []).filter(
      id => id !== descriptor.imageId,
    );
    this.save();
  }

  get sceneId() {
    return this.scene.id;
  }

  get sceneIndex() {
    return this.scene.index;
  }

  get text() {
    return this.scene.texts;
  }

  get sceneLayout() {
    const { texts: text, images, videoInstances: videos } = this.scene;
    const onlyText = text && !images?.length && !videos?.length;

    if (onlyText) {
      return 'only-text';
    }
    // return this.scene.layout;
  }

  /**
   * Returns the animation context for this scene.
   * Used by animation generation services to understand scene content.
   */
  getContext(): AnimationContext {
    const { texts, images, videoInstances, positionIndex } = this.scene;

    // Determine primary element type
    let primaryElement: 'text' | 'image' | 'video' = 'text';
    if (videoInstances?.length > 0) {
      primaryElement = 'video';
    } else if (images?.length > 0) {
      primaryElement = 'image';
    }

    // Determine content density
    const totalElements =
      (texts?.length || 0) +
      (images?.length || 0) +
      (videoInstances?.length || 0);
    let contentDensity: 'sparse' | 'balanced' | 'dense' = 'balanced';
    if (totalElements <= 1) {
      contentDensity = 'sparse';
    } else if (totalElements >= 4) {
      contentDensity = 'dense';
    }

    // Build text context if texts exist
    let textContext: AnimationContext['graphics']['text'] | undefined;
    if (texts?.length > 0) {
      const textId = texts[0];
      const textShape = this.shapes[textId] as StudioTextShape;
      if (textShape) {
        const descriptor = textShape.descriptor as StudioTextShapeDescriptor;
        const textContent = descriptor.text || '';
        const words = textContent.split(/\s+/).filter(w => w.length > 0);
        const lines = textContent.split('\n').filter(l => l.length > 0);
        const longestWord = words.reduce(
          (max, word) => (word.length > max ? word.length : max),
          0,
        );

        textContext = {
          count: texts.length,
          wordCount: words.length,
          lineCount: lines.length || 1,
          hasEmphasis: false, // Could be extended to detect bold/italic
          longestWordLength: longestWord,
        };
      }
    }

    // Build image context if images exist
    let imageContext: AnimationContext['graphics']['image'] | undefined;
    if (images?.length > 0) {
      const imageId = images[0];
      const imageShape = this.shapes[imageId] as StudioImageShape;
      if (imageShape) {
        const descriptor = imageShape.descriptor as StudioImageShapeDescriptor;
        const { cell } = descriptor;
        // Calculate aspect ratio from cell dimensions
        const aspectRatio = cell?.w && cell?.h ? cell.w / cell.h : 1;

        imageContext = {
          count: images.length,
          aspectRatio,
          hasTransparency: false,
        };
      }
    }

    // Get total scenes count
    const totalScenes = (this.studioService.scenesByPositionIndex || []).filter(
      shape => shape.scene.id !== 'add-after-last',
    ).length;

    // Determine scene role based on position
    let role: 'intro' | 'body' | 'emphasis' | 'conclusion' | undefined;
    if (positionIndex === 0) {
      role = 'intro';
    } else if (positionIndex === totalScenes - 1) {
      role = 'conclusion';
    } else {
      role = 'body';
    }

    return {
      graphics: {
        text: textContext,
        image: imageContext,
        layout: {
          contentDensity,
          primaryElement,
          alignment: 'center',
        },
      },
      animation: this.scene.animation,
      scene: {
        positionIndex: positionIndex || 0,
        totalScenes,
        role,
        duration: this.animationDuration,
      },
    };
  }

  show() {
    this.container.visible = true;
    this.bgContainer.visible = true;
  }

  hide() {
    this.container.visible = false;
    this.bgContainer.visible = false;
  }

  remove() {
    console.warn('studio-scene-shape > remove');
    // Stop the local timer if running
    this.container.destroy({ children: true });
    this.bgContainer.destroy({ children: true });
  }

  cleanCells() {
    const screenW = this.screenWidth;
    const screenH = this.screenHeight;

    Object.entries(this.shapes).forEach(([id, shape]) => {
      const descriptor = this.scene.shapes[id];
      if (!descriptor?.cell) return;

      const cell = descriptor.cell;
      const cellW = screenW * cell.w;
      const cellH = screenH * cell.h;

      let contentW: number | null = null;
      let contentH: number | null = null;

      if ('maxLineWidth' in shape && 'textHeight' in shape) {
        const textShape = shape as StudioTextShape;
        contentW = textShape.maxLineWidth;
        contentH = textShape.textHeight;
      } else {
        // Images/videos fill their cell, nothing to clean
        return;
      }

      if (contentW == null || contentH == null) return;

      const needsCleanW = contentW < cellW;
      const needsCleanH = contentH < cellH;

      if (!needsCleanW && !needsCleanH) return;

      const newW = needsCleanW ? contentW / screenW : cell.w;
      const newH = needsCleanH ? contentH / screenH : cell.h;

      // Center the new cell within the old cell
      const newX = cell.x + (cell.w - newW) / 2;
      const newY = cell.y + (cell.h - newH) / 2;

      console.log(`[cleanCells] Shape "${id}":`, {
        previous: { x: cell.x, y: cell.y, w: cell.w, h: cell.h },
        new: { x: newX, y: newY, w: newW, h: newH },
        cellPx: { w: cellW, h: cellH },
        contentPx: { w: contentW, h: contentH },
      });
    });
  }

  autoLayout() {
    const { texts, images, videoInstances, elements } = this.scene;
    const allIds: string[] = [
      ...(videoInstances || []),
      ...(images || []),
      ...(elements || []),
      ...(texts || []),
    ];

    if (allIds.length === 0) return;

    const children: LayoutCellDefinition[] = allIds.map(id => ({
      shape: id,
    }));

    const definition: LayoutCellDefinition = {
      type: 'column',
      alignment: 'center',
      w: 1,
      h: 1,
      children,
    };

    this.scene.layoutDefinition = definition;
    this.layout = new RootLayoutCell(
      this.scene,
      definition,
      { W: this.screenWidth, H: this.screenHeight },
    );

    // Update each shape's cell from the layout
    allIds.forEach(id => {
      const cell = this.layout.cellsById[id];
      if (!cell) return;
      const abs = cell.absoluteDims;
      if (!abs || !this.scene.shapes[id]) return;
      this.scene.shapes[id].cell = {
        ...this.scene.shapes[id].cell,
        x: abs.x,
        y: abs.y,
        w: abs.w,
        h: abs.h,
      };
    });

    this.initShapes();
    this.save();
  }

  pushShape(shape: any) {
    this._shapes.push(shape);
  }
}
